import React, { useState, useEffect } from 'react';
import axios from 'axios';

import { useLazyLoad, useSocketContext } from '@techchange/components';

const axiosClient = axios.create({
	withCredentials: true,
});

const POINT_TOTALS_LIMIT = 15;

/**
 * React hook that keeps track of a paginated list of point totals.
 *
 * @param {Object} params            The base query params.
 * @param {String} params.user_id    The user id.
 * @param {String} params.scope      The scope of the point totals.
 * @param {Array}  [params.include]  The model(s) to include in the results (if any).
 *
 * @param {String} nodeID  The unique ID of the DOM element to listen for scroll events.
 *
 * @returns {Array} The data, error state, lazy load state,
 *                  and a callback function to fetch the data.
 */
const usePaginatedPointTotals = (params = {}, nodeID) => {

	const [pointTotals, setPointTotals] = useState(null);
	const [showError, setShowError] = useState(false);

	const [offset, setOffset] = useState(0);
	const [loadState, setLoadState] = useLazyLoad(nodeID);

	const { socket } = useSocketContext();

	const queryParams = Object.assign({}, params, {
		sort_by: 'total',
		sort_order: 'DESC',
		limit: POINT_TOTALS_LIMIT,
	});

	const fetchData = () => {

		axiosClient.get('/api/v2/point-totals', { params: queryParams }).then(({ data }) => {

			setLoadState((prevLoadState) => Object.assign({}, prevLoadState, {
				ready: true,
				loading: false,
				loadedAll: data.length < POINT_TOTALS_LIMIT,
			}));

			setOffset(offset + POINT_TOTALS_LIMIT);

			setPointTotals(data);

		}).catch((error) => {

			console.error(error);
			setShowError(true);

		});

	};

	// Handle lazy loads triggered by scrolling down the list of point totals
	useEffect(() => {

		if (!loadState.loading) {
			return;
		}

		// Update the query params to include the pagination offset
		const finalQueryParams = Object.assign({}, queryParams, {
			offset,
		});

		axiosClient.get('/api/v2/point-totals', { params: finalQueryParams }).then(({ data }) => {

			setLoadState((prevLoadState) => Object.assign({}, prevLoadState, {
				ready: true,
				loading: false,
				loadedAll: data.length < POINT_TOTALS_LIMIT,
			}));

			setOffset(offset + POINT_TOTALS_LIMIT);

			setPointTotals((prevPointTotals) => prevPointTotals.concat(data));

		}).catch((error) => {

			console.error(error);
			setShowError(true);

		});

	}, [loadState.loading, offset, queryParams]);

	// Listen for real-time changes to the point totals
	useEffect(() => {

		if (!pointTotals) {
			return () => {};
		}

		const handleRealTimeUpdate = (pointLog) => {

			// Check that the point total changed for the current user
			if (pointLog.user_id !== params.user_id) {
				return;
			}

			// Check if a point total in the list changed for the current user
			const pointTotal =
				pointLog.point_totals &&
				pointLog.point_totals.find((total) => total.scope === params.scope);

			// Find and update the point total that changed!
			if (pointTotal) {

				const updatedPointTotals = [...pointTotals];

				const index = updatedPointTotals.findIndex((total) => total.id === pointTotal.id);

				if (index >= 0) {

					updatedPointTotals[index] = Object.assign({},
						updatedPointTotals[index],
						{ total: pointTotal.total }
					);

					setPointTotals(updatedPointTotals);

				}

			}

		};

		// Use a socket to start listening for real-time changes
		socket?.on('create:point-log', handleRealTimeUpdate);

		// Clean up the socket to stop listening for real-time changes
		return () => socket?.removeListener('create:point-log', handleRealTimeUpdate);

	}, [pointTotals, params.user_id, params.scope, socket]);

	return [pointTotals, showError, loadState, fetchData];

};

export default usePaginatedPointTotals;
